import type { Pilot } from '../physics/Pilot'
import type { FirstFlight } from '../game/firstFlight'
import type { HandOffset } from '../input/GamepadSource'

/**
 * Desktop HUD: frame rate (perf discipline from day 1, §8), flight state, and
 * the First Flight timer/crash counter that is the whole Phase 1 game loop.
 */
export class Hud {
  private frames = 0
  private accumulated = 0
  private fps = 0
  /** Worst frame time in the current window — averages hide the stutters. */
  private worstMs = 0

  constructor(private readonly root: HTMLElement) {}

  update(
    dt: number,
    pilot: Pilot,
    flight: FirstFlight,
    extra: {
      mode: string
      input: string
      grip: string
      preset: string
      replay?: string
      offset?: HandOffset
      worldAim?: boolean
    },
  ): void {
    this.frames++
    this.accumulated += dt
    this.worstMs = Math.max(this.worstMs, dt * 1000)
    if (this.accumulated >= 0.5) {
      this.fps = this.frames / this.accumulated
      this.frames = 0
      this.accumulated = 0
    }

    const altitude = pilot.altitude
    const speed = pilot.body.velocity.length()
    const tilt = pilot.tiltDegrees()
    const fpsClass = this.fps >= 80 ? 'good' : this.fps >= 55 ? 'warn' : 'bad'
    const tiltClass = tilt > 60 ? 'bad' : tilt > 25 ? 'warn' : ''

    const throttles = pilot.debug.rockets
      .map((r) => `${bar(r.level)}${r.tracked ? '' : ' ⚠'}`)
      .join('  ')

    const lines: string[] = [
      `<span class="big">${flight.elapsed.toFixed(1)}s</span> <span class="dim">timer</span>`,
      `<span class="dim">crashes</span> ${flight.crashes}` +
        (flight.bestTime !== null ? `   <span class="dim">best</span> ${flight.bestTime.toFixed(1)}s` : ''),
      '',
      `<span class="${fpsClass}">${this.fps.toFixed(0)} fps</span> <span class="dim">worst ${this.worstMs.toFixed(1)} ms</span>`,
      `<span class="dim">alt</span> ${altitude.toFixed(1)} m   <span class="dim">spd</span> ${speed.toFixed(1)} m/s`,
      `<span class="dim">tilt</span> <span class="${tiltClass}">${tilt.toFixed(0)}°</span>   <span class="dim">hover</span> ${(pilot.hoverThrottle * 100).toFixed(0)}%`,
      `<span class="dim">thr</span> ${throttles}`,
      '',
      `<span class="dim">${extra.mode} · ${extra.input} · ${extra.preset}</span>`,
      `<span class="dim">grip</span> ${extra.grip}${formatOffset(extra.offset)}` +
        (extra.worldAim ? '   <span class="warn">world-down aim</span>' : ''),
      `<span class="dim">${flight.state}</span>${flight.lastMessage ? `  <span class="warn">${flight.lastMessage}</span>` : ''}`,
    ]
    if (extra.replay) lines.push(`<span class="good">${extra.replay}</span>`)

    this.root.innerHTML = lines.join('\n')

    if (this.frames === 0) this.worstMs = 0
  }
}

/** Only shows the axes that have actually been nudged off the preset. */
function formatOffset(offset: HandOffset | undefined): string {
  if (!offset) return ''
  const parts: string[] = []
  if (Math.abs(offset.height) > 0.005) parts.push(`h${signed(offset.height)}`)
  if (Math.abs(offset.spread) > 0.005) parts.push(`w${signed(offset.spread)}`)
  if (Math.abs(offset.forward) > 0.005) parts.push(`f${signed(offset.forward)}`)
  return parts.length ? ` <span class="warn">${parts.join(' ')}</span>` : ''
}

function signed(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}`
}

function bar(level: number): string {
  const filled = Math.round(level * 8)
  return '█'.repeat(filled) + '░'.repeat(8 - filled)
}
